import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, User, Package, Zap, Settings, Info } from 'lucide-react';
import { panelRequestAPI } from '../../services/api';
import { Alert } from '../common/Alert';
import { Input } from '../common/Input';
import { Button } from '../common/Button';
import { RecaptchaComponent } from '../common/RecaptchaComponent';

interface PanelRequestFormProps {
  onClose: () => void;
  onSuccess?: () => void;
}

interface PanelRequestData {
  // Contact Information
  email: string;
  name: string;
  phone: string;
  company: string;
  
  // Panel Specifications
  brand: string;
  model: string;
  wattage: number;
  efficiency: number;
  technology: string;
  
  // Physical Specifications
  dimensions: string;
  weight: number;
  cells: number;
  
  // Electrical Specifications
  vmp: number;
  imp: number;
  voc: number;
  isc: number;
  
  // Additional Information
  description: string;
  urgency: 'low' | 'medium' | 'high';
}

export const PanelRequestForm: React.FC<PanelRequestFormProps> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState<PanelRequestData>({
    email: '',
    name: '',
    phone: '',
    company: '',
    brand: '',
    model: '',
    wattage: 0,
    efficiency: 0,
    technology: '',
    dimensions: '',
    weight: 0,
    cells: 0,
    vmp: 0,
    imp: 0,
    voc: 0,
    isc: 0,
    description: '',
    urgency: 'medium'
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [recaptchaError, setRecaptchaError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: ['wattage', 'efficiency', 'weight', 'cells', 'vmp', 'imp', 'voc', 'isc'].includes(name) 
        ? Number(value) 
        : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Check if reCAPTCHA is completed
      if (!recaptchaToken) {
        setRecaptchaError('Please complete the reCAPTCHA verification');
        setLoading(false);
        return;
      }

      await panelRequestAPI.requestPanel({ ...formData, recaptchaToken });
      setSuccess(true);
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit panel request');
    } finally {
      setLoading(false);
    }
  };

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
    if (token) {
      setRecaptchaError('');
    }
  };

  const handleRecaptchaError = () => {
    setRecaptchaError('reCAPTCHA verification failed. Please try again.');
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaError('reCAPTCHA expired. Please verify again.');
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-lg p-8 max-w-md w-full mx-4"
        >
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Request Submitted!</h3>
            <p className="text-gray-600 mb-6">
              Your panel request has been submitted successfully. We'll review it and get back to you at {formData.email}.
            </p>
            <Button onClick={onClose} className="w-full">
              Close
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">Request Panel Addition</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {error && <Alert type="error" message={error} onClose={() => setError('')} />}
          
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Contact Information */}
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <User className="w-5 h-5 text-[#63B56F] mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Contact Information</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Email Address *"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="your@email.com"
                />
                <Input
                  label="Full Name *"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="John Doe"
                />
                <Input
                  label="Phone Number"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+1 (555) 123-4567"
                />
                <Input
                  label="Company"
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="Your Company Name"
                />
              </div>
            </div>

            {/* Panel Specifications */}
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <Package className="w-5 h-5 text-[#63B56F] mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Panel Specifications</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Input
                  label="Brand *"
                  type="text"
                  name="brand"
                  value={formData.brand}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., SunPower"
                />
                <Input
                  label="Model *"
                  type="text"
                  name="model"
                  value={formData.model}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., SPR-MAX3-400"
                />
                <Input
                  label="Wattage (W) *"
                  type="number"
                  name="wattage"
                  value={formData.wattage}
                  onChange={handleInputChange}
                  required
                  min="1"
                  placeholder="400"
                />
                <Input
                  label="Efficiency (%)"
                  type="number"
                  name="efficiency"
                  value={formData.efficiency}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.1"
                  placeholder="22.8"
                />
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Technology
                  </label>
                  <select
                    name="technology"
                    value={formData.technology}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#63B56F] focus:border-[#63B56F]"
                  >
                    <option value="">Select Technology</option>
                    <option value="Monocrystalline">Monocrystalline</option>
                    <option value="Polycrystalline">Polycrystalline</option>
                    <option value="Thin-Film">Thin-Film</option>
                    <option value="Bifacial">Bifacial</option>
                    <option value="PERC">PERC</option>
                    <option value="HJT">HJT</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Physical Specifications */}
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <Settings className="w-5 h-5 text-[#63B56F] mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Physical Specifications</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Input
                  label="Dimensions"
                  type="text"
                  name="dimensions"
                  value={formData.dimensions}
                  onChange={handleInputChange}
                  placeholder="2008 x 1002 x 40 mm"
                />
                <Input
                  label="Weight (kg)"
                  type="number"
                  name="weight"
                  value={formData.weight}
                  onChange={handleInputChange}
                  min="0"
                  step="0.1"
                  placeholder="20.5"
                />
                <Input
                  label="Number of Cells"
                  type="number"
                  name="cells"
                  value={formData.cells}
                  onChange={handleInputChange}
                  min="0"
                  placeholder="72"
                />
              </div>
            </div>

            {/* Electrical Specifications */}
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <Zap className="w-5 h-5 text-[#63B56F] mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Electrical Specifications</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Input
                  label="Vmp (V)"
                  type="number"
                  name="vmp"
                  value={formData.vmp}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  placeholder="32.5"
                />
                <Input
                  label="Imp (A)"
                  type="number"
                  name="imp"
                  value={formData.imp}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  placeholder="12.3"
                />
                <Input
                  label="Voc (V)"
                  type="number"
                  name="voc"
                  value={formData.voc}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  placeholder="40.5"
                />
                <Input
                  label="Isc (A)"
                  type="number"
                  name="isc"
                  value={formData.isc}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  placeholder="13.0"
                />
              </div>
            </div>

            {/* Additional Information */}
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <Info className="w-5 h-5 text-[#63B56F] mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Additional Information</h3>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#63B56F] focus:border-[#63B56F]"
                    placeholder="Any additional details about this panel request..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Urgency Level
                  </label>
                  <select
                    name="urgency"
                    value={formData.urgency}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#63B56F] focus:border-[#63B56F]"
                  >
                    <option value="low">Low - No rush</option>
                    <option value="medium">Medium - Within a few days</option>
                    <option value="high">High - Urgent</option>
                  </select>
                </div>
              </div>
            </div>

            {/* reCAPTCHA verification */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="mb-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Security Verification *
                </label>
                <RecaptchaComponent
                  onVerify={handleRecaptchaChange}
                  onError={handleRecaptchaError}
                  onExpired={handleRecaptchaExpired}
                />
                {recaptchaError && (
                  <p className="mt-2 text-sm text-red-600">{recaptchaError}</p>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={loading}
                disabled={!formData.email || !formData.name || !formData.brand || !formData.model || !formData.wattage || !recaptchaToken}
              >
                Submit Request
              </Button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};
